import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAuth } from '../contexts/Auth';
import {
  isDelegato,
  isDelegatoSezione,
  isSegreteriaNazionale,
} from '../libs/utils/auth';
import { formatCurrency } from '../libs/utils/formatters/currency';
import BalanceTable from '../pages/Corsi/Tables/BalanceTable';
import HeadquarterBalanceTable from '../pages/Corsi/Tables/HeadquarterBalanceTable';
import UnderlineButton from './Buttons/UnderlineButton';
import InputPresentational from './Form/InputPresentational';
import PriceInputPresentational from './Form/PriceInputPresentational';
import RadioGroupPaymentCourse from './Form/RadioGroupPaymentCourse';
import StaticInputPresentational from './Form/StaticInputPresentational';
import H4SpacingLetter from './Typography/H4SpacingLetter';
import { DownloadIcon } from '@heroicons/react/outline';
import { CourseStatus } from '../interfaces/commons';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import AlertMessage from './AlertMessage';
import { CourseRequest } from '../interfaces/courses';
import { ParticipantStatus } from '../interfaces/participants';
import { useMutation } from 'react-query';
import coursesAPIs from '../api/courses';
import { useParams } from 'react-router-dom';
import {
  calcoloTotSpeseSedeSede,
  calcoloTotSpeseSezioneSede,
  calcoloTotSpeseSezioneSezione,
  getCourseSubscriptionLimit,
} from '../libs/utils/helpers';
import CustomSwitch from './Form/CustomSwitch';
import SimpleCheckboxPresentational from './Form/SimpleCheckboxPresentational';
import BaseModal from './Modals/BaseModal';

interface FormCostiEventiProps {
  estimatedIncome: number;
  estimatedOutflow: number;
  finalIncome: number;
  finalOutflow: number;
  estimatedHeadquarterOutflow: number;
  estimatedHeadquarterIncome: number;
  finalHeadquarterOutflow: number;
  finalHeadquarterIncome: number;
  disabled?: boolean;
  courseStatus?: CourseStatus;
  safetyMargin?: number;
  disableEditConsuntivo: boolean;
  sectionIban: string;
  currentConfirmedAndPendingParticipants?: number;
  originalMinParticipants?: number;
  originalEstimatedTeacherPayment?: number;
  originalEstimatedTeacherRefund?: number;
}

const FormCostiCorso: React.FC<FormCostiEventiProps> = ({
  estimatedIncome,
  estimatedOutflow,
  finalIncome,
  finalOutflow,
  estimatedHeadquarterOutflow,
  estimatedHeadquarterIncome,
  finalHeadquarterIncome,
  finalHeadquarterOutflow,
  disabled,
  courseStatus,
  safetyMargin,
  disableEditConsuntivo,
  sectionIban,
  currentConfirmedAndPendingParticipants = 0,
  originalMinParticipants,
  originalEstimatedTeacherPayment,
  originalEstimatedTeacherRefund,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext<CourseRequest>();
  const [{ profile, settings, token }] = useAuth();
  const { id: courseId } = useParams();

  const priceLimit = useMemo(() => {
    const balance = watch('balance');
    const headquarterBalance = watch('headquarterBalance');
    const minParticipants = watch('minParticipants');
    const initialSubscriptionAmount = watch('initialSubscriptionAmount') || 0;

    return getCourseSubscriptionLimit(
      balance,
      headquarterBalance,
      minParticipants,
      initialSubscriptionAmount,
    );
  }, [
    watch('minParticipants'),
    watch('initialSubscriptionAmount'),
    watch('bottlePrice'),
    watch('glassesForBottle'),
    watch('wineTypes'),
    // Campi balance che influiscono su estimatedOutflow
    watch('balance.food.estimatedPriceUnit'),
    watch('balance.rooms.estimatedOutflow'),
    watch('balance.discountExpenses.estimatedOutflow'),
    watch('balance.sectionVariousExpenses.estimatedOutflow'),
    watch('balance.couponsExpenses.estimatedOutflow'),
    watch('balance.sponsorship.estimatedOutflow'),
    watch('balance.wines.estimatedOutflow'),
    watch('balance.teacherPayments.estimatedOutflow'),
    watch('balance.teacherRefunds.estimatedOutflow'),
    watch('balance.contributes.estimatedIncome'),
    // Campi headquarterBalance che influiscono su estimatedHeadquarterOutflow
    watch('headquarterBalance.teachingMaterial.estimatedPriceUnit'),
    watch('headquarterBalance.administrations.estimatedPriceUnit'),
    watch('headquarterBalance.administrations.estimatedIncome'),
    watch('headquarterBalance.shipping.estimatedOutflow'),
    watch('headquarterBalance.reimbursement.estimatedOutflow'),
    watch('headquarterBalance.safetyMargin.estimatedOutflow'),
  ]);

  const [limitDebounce] = useDebounce(priceLimit, 1000);
  const [showAlert, setShowAlert] = useState(false);
  const [confirmDisableSubscriptionModal, setConfirmDisableSubscriptionModal] =
    useState(false);
  const totalConfirmedParticipants = useMemo(
    () =>
      watch('partecipations')?.data.filter(
        (elem) => elem.attributes.status === ParticipantStatus.CONFIRMED,
      ).length || 0,
    [watch('partecipations')],
  );

  const totalPendingParticipants = useMemo(
    () =>
      watch('partecipations')?.data.filter(
        (elem) => elem.attributes.status === ParticipantStatus.PENDING,
      ).length || 0,
    [watch('partecipations')],
  );

  const maxParticipants = Number(watch('maxParticipants') || 0);
  const isCourseFull =
    maxParticipants > 0 &&
    (currentConfirmedAndPendingParticipants ?? totalConfirmedParticipants + totalPendingParticipants) >= maxParticipants;

  const speseSedeSede = useMemo(
    () => calcoloTotSpeseSedeSede(watch('headquarterBalance')),
    [
      estimatedIncome,
      estimatedHeadquarterIncome,
      finalIncome,
      finalHeadquarterOutflow,
    ],
  );

  const speseSezioneSezione = useMemo(
    () => calcoloTotSpeseSezioneSezione(watch('balance')),
    [
      estimatedIncome,
      estimatedHeadquarterIncome,
      finalIncome,
      finalHeadquarterOutflow,
    ],
  );

  const speseSezioneSede = useMemo(
    () => calcoloTotSpeseSezioneSede(watch('balance')),
    [
      estimatedIncome,
      estimatedHeadquarterIncome,
      finalIncome,
      finalHeadquarterOutflow,
    ],
  );

  const utilePreventivato = useMemo(() => {
    return (
      Number(estimatedIncome || 0) +
      Number(estimatedHeadquarterIncome || 0) -
      (Number(estimatedOutflow || 0) + Number(estimatedHeadquarterOutflow || 0))
    );
  }, [
    estimatedIncome,
    estimatedHeadquarterIncome,
    estimatedOutflow,
    estimatedHeadquarterOutflow,
  ]);

  const utileConsuntivato = useMemo(
    () =>
      Number(finalIncome || 0) +
      Number(finalHeadquarterIncome || 0) -
      (Number(finalOutflow || 0) + Number(finalHeadquarterOutflow || 0)),
    [
      finalIncome,
      finalHeadquarterIncome,
      finalOutflow,
      finalHeadquarterOutflow,
    ],
  );

  const totSpese = useMemo(
    () => speseSedeSede + speseSezioneSede + speseSezioneSezione,
    [speseSedeSede, speseSezioneSede, speseSezioneSezione],
  );

  const subscriptionAmount = watch('subscriptionAmount') || 0;
  const initialSubscriptionAmount = watch('initialSubscriptionAmount') || 0;
  const isFreeCourse = !!watch('freeCourse');
  const TransferSum = subscriptionAmount ?? initialSubscriptionAmount;

  const TransferMarkup = isFreeCourse
    ? 0
    : TransferSum * (settings?.bankTransferFee || 0.2);
  const bankTransferTotal = isFreeCourse
    ? 0
    : Math.ceil(TransferSum + TransferMarkup);

  const onChangeSubscriptionAmount = (value: number, limit?: number) => {
    if (watch('status') !== CourseStatus.DRAFT) return;
    const isFreeCourse = watch('freeCourse');
    if (isFreeCourse && Number(value) === 0) {
      setValue('subscriptionAmount', 0);
      clearErrors('subscriptionAmount');
      setShowAlert(false);
      return;
    }
    const currentLimit = limit ?? priceLimit;
    if (Number(value) < currentLimit) {
      setShowAlert(true);
      toast.error(
        "La quota corso non può essere minore dell'importo suggerito",
      );
      setError('subscriptionAmount', {
        message: "La quota corso non può essere minore dell'importo suggerito",
      });
      // setValue('subscriptionAmount', currentLimit);
      // setTimeout(() => {
      //   setShowAlert(false);
      // }, 5000);
      return;
    }
    if (Number(value) >= currentLimit) {
      setValue('subscriptionAmount', Math.ceil(Number(value)));
      clearErrors('subscriptionAmount');
      setShowAlert(false);
    }
  };

  const { mutate: downloadBalanceCSVMutation } = useMutation(
    ['downloadBalanceCSV'],
    coursesAPIs.downloadCostsCSV,
    {
      onSuccess: () => {
        toast.success('Operazione avvenuta con successo');
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error?.message ||
            'Ooops... Qualcosa è andato storto',
        );
      },
    },
  );

  useEffect(() => {
    const initializationKey = `course_${courseId}_subscription_initialized`;
    const pageLoadTimestampKey = 'global_page_load_timestamp';

    // Ottieni o crea il timestamp globale del caricamento della pagina
    let pageLoadTimestamp = sessionStorage.getItem(pageLoadTimestampKey);
    const currentPageLoadTimestamp =
      (window as any).__PAGE_LOAD_TIMESTAMP__ || Date.now().toString();

    // Se non esiste un timestamp salvato o è diverso da quello corrente, è un nuovo caricamento
    if (!pageLoadTimestamp || pageLoadTimestamp !== currentPageLoadTimestamp) {
      // Nuovo caricamento della pagina - salva il timestamp globale
      sessionStorage.setItem(pageLoadTimestampKey, currentPageLoadTimestamp);
      (window as any).__PAGE_LOAD_TIMESTAMP__ = currentPageLoadTimestamp;
      // Resetta il flag di inizializzazione per questo nuovo caricamento
      sessionStorage.removeItem(initializationKey);
    }

    const hasInitialized = sessionStorage.getItem(initializationKey) === 'true';

    // Se è già stato inizializzato in questo caricamento della pagina, non fare nulla
    if (hasInitialized) {
      return;
    }

    const subscriptionAmount = watch('subscriptionAmount');
    const isFreeCourse = watch('freeCourse');
    if (isFreeCourse && Number(subscriptionAmount) === 0) {
      return;
    }
    if (Number(subscriptionAmount) > 0) {
      // Simula come se l'utente avesse modificato il campo
      // Impostando lo stesso valore triggera la validazione con il nuovo limite
      setValue('subscriptionAmount', subscriptionAmount, {
        shouldValidate: true,
      });
      // Poi chiama la funzione di validazione con il limite ricalcolato
      onChangeSubscriptionAmount(subscriptionAmount, limitDebounce);
    } else {
      setValue(
        'subscriptionAmount',
        Number(watch('initialSubscriptionAmount') || 0),
      );
    }
  }, [limitDebounce, watch('freeCourse')]);

  useEffect(() => {
    if (watch('status') !== CourseStatus.DRAFT) {
      const maxParticipants = watch('maxParticipants');
      const minParticipants = Number(watch('minParticipants'));

      // Controlla se maxParticipants è inferiore al numero minimo
      if (maxParticipants < minParticipants) {
        setError('maxParticipants', {
          message:
            'Il numero massimo di partecipanti non può essere inferiore al numero minimo',
        });
      }
      // Controlla se maxParticipants è inferiore al numero di partecipanti confermati + in attesa (corsi)
      else if (maxParticipants < currentConfirmedAndPendingParticipants) {
        setError('maxParticipants', {
          message: `Il numero massimo di partecipanti non può essere inferiore al numero di partecipanti confermati/in attesa (${currentConfirmedAndPendingParticipants})`,
        });
      } else {
        clearErrors('maxParticipants');
      }
    } else {
      clearErrors('maxParticipants');
    }
  }, [watch('maxParticipants'), currentConfirmedAndPendingParticipants]);

  const toggleConfirmDisableSubscriptions = () =>
    setConfirmDisableSubscriptionModal((v) => !v);

  const isSimulationStatusCourse =
    courseStatus === CourseStatus.DRAFT ||
    courseStatus === CourseStatus.TO_APPROVE ||
    courseStatus === CourseStatus.PUBLISHED;
  const canSimulateMin =
    isSegreteriaNazionale(profile) &&
    isSimulationStatusCourse &&
    originalMinParticipants !== undefined;
  const shouldPreventDecreaseForNonSegreteria =
    [CourseStatus.TO_APPROVE, CourseStatus.PUBLISHED].includes(
      courseStatus as CourseStatus
    ) &&
    !isSegreteriaNazionale(profile) &&
    originalMinParticipants !== undefined;
  const isSimulatedMin =
    canSimulateMin &&
    Number(watch('minParticipants') || 0) !== Number(originalMinParticipants);
  const isMinParticipantsDisabled = !!(disabled && !canSimulateMin);
  const isDraftCourse = watch('status') === CourseStatus.DRAFT;

  return (
    <div className='pt-6 pb-8 space-y-8'>
      <div className='space-y-4'>
        <H4SpacingLetter>Numero di partecipanti</H4SpacingLetter>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 items-start gap-4'>
          <div className='space-y-2'>
            <InputPresentational
              id='minParticipants'
              label='Numero minimo di partecipanti'
              name='minParticipants'
              type='number'
              register={register}
              disabled={isMinParticipantsDisabled}
              error={errors.minParticipants}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!shouldPreventDecreaseForNonSegreteria) return;

                const value = Number(e.target.value || 0);
                const minValue = Number(originalMinParticipants || 0);

                if (value < minValue) {
                  setValue('minParticipants', minValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setError('minParticipants', {
                    message: `Il numero minimo di partecipanti non può essere inferiore al valore iniziale (${minValue})`,
                  });
                  toast.error(
                    `Il numero minimo di partecipanti non può essere inferiore al valore iniziale (${minValue})`,
                  );
                } else {
                  clearErrors('minParticipants');
                }
              }}
            />
            {canSimulateMin && isSimulatedMin && (
              <div className='flex flex-wrap items-center gap-3'>
                <p className='mb-0 text-xs text-gray-500'>
                  Numero minimo originale: {originalMinParticipants}
                </p>
                <UnderlineButton
                  type='button'
                  small
                  className='!text-xs'
                  onClick={() =>
                    setValue(
                      'minParticipants',
                      Number(originalMinParticipants),
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      },
                    )
                  }
                >
                  Ripristina
                </UnderlineButton>
              </div>
            )}
          </div>
          <InputPresentational
            id='maxParticipants'
            label='Numero massimo di partecipanti'
            name='maxParticipants'
            type='number'
            disabled={
              watch('status') === CourseStatus.CANCELED ? disabled : false
            }
            register={register}
            error={errors.maxParticipants}
          />
        </div>
      </div>
      <div className='space-y-4'>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm text-gray-500 whitespace-nowrap'>
              Numero di partecipanti CONFERMATI
            </label>
            <InputPresentational
              type='number'
              defaultValue={totalConfirmedParticipants}
              disabled
            />
          </div>
          <div>
            <label className='block text-sm text-gray-500 whitespace-nowrap'>
              Numero di partecipanti IN ATTESA
            </label>
            <InputPresentational
              type='number'
              defaultValue={totalPendingParticipants}
              disabled
            />
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-6'>
          <CustomSwitch
            checked={!!watch('disableSubscriptions')}
            onChange={() => {
              toggleConfirmDisableSubscriptions();
            }}
            label='Disabilita iscrizioni'
          />
          <CustomSwitch
            checked={!!watch('subscriptionsOnlyWaitlist')}
            onChange={() => {
              const next = !watch('subscriptionsOnlyWaitlist');
              if (next && isCourseFull) {
                toast.warning(
                  'Prima di poter procedere aumenta il numero massimo di partecipanti.'
                );
                return;
              }
              setValue('subscriptionsOnlyWaitlist', next);
            }}
            label="Attiva iscrizioni solo per lista d'attesa"
          />
        </div>
        <BaseModal
          isOpen={confirmDisableSubscriptionModal}
          toggle={toggleConfirmDisableSubscriptions}
          onConfirm={() => {
            toggleConfirmDisableSubscriptions();
            setValue('disableSubscriptions', !watch('disableSubscriptions'));
          }}
          title={`${
            watch('disableSubscriptions') ? 'Attivazione' : 'Sospensione'
          } iscrizioni evento`}
          subtitle={`Sei sicuro di voler <strong>${
            watch('disableSubscriptions') ? 'attivare' : 'sospendere'
          }</strong> le iscrizioni per il corso ${watch(
            'title',
          )}? </br>Questa variazione sarà valida solo dopo il salvataggio del corso.`}
          onCancel={toggleConfirmDisableSubscriptions}
        />
      </div>

      <div className='space-y-4'>
        <H4SpacingLetter>Liquidi</H4SpacingLetter>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 items-end gap-4'>
          <InputPresentational
            id='glassesForBottle'
            label='Numero bicchieri per bottiglia'
            name='glassesForBottle'
            type='number'
            register={register}
            disabled={disabled}
          />
          <InputPresentational
            id='wineTypes'
            label='Numero tipologie di vino'
            name='wineTypes'
            type='number'
            register={register}
            disabled={disabled}
          />
          <PriceInputPresentational
            id='bottlePrice'
            label='Prezzo medio a bottiglia in €'
            name='bottlePrice'
            type='number'
            register={register}
            disabled={disabled}
          />
        </div>
      </div>
      <div className='space-y-4'>
        <div className='flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-between gap-4'>
          <H4SpacingLetter>Bilancio</H4SpacingLetter>
          {courseId && isSegreteriaNazionale(profile) && (
            <UnderlineButton
              onClick={() => {
                downloadBalanceCSVMutation({
                  token,
                  id: courseId,
                  fileName: `BILANCIO CORSO ${watch(
                    'title',
                  )}_${new Date().getTime()}`,
                });
              }}
            >
              <DownloadIcon className='w-4 h-4' /> Scarica CSV bilancio
            </UnderlineButton>
          )}
        </div>

        <BalanceTable
          type='corso'
          estimatedOutflow={estimatedOutflow || 0}
          estimatedIncome={estimatedIncome}
          finalOutflow={finalOutflow}
          finalIncome={finalIncome}
          disabled={disabled && disableEditConsuntivo}
          canEditEstimatedTeacherBudget={
            isSegreteriaNazionale(profile) &&
            [CourseStatus.DRAFT, CourseStatus.PUBLISHED].includes(
              watch('status')
            )
          }
          originalEstimatedTeacherPayment={originalEstimatedTeacherPayment}
          originalEstimatedTeacherRefund={originalEstimatedTeacherRefund}
          //per isCoursePublished si intende se il corso è stato pubblicato o consuntivato
          isCoursePublished={[
            CourseStatus.PUBLISHED,
            CourseStatus.FINAL_BALANCE_DELEGATION,
            CourseStatus.FINAL_BALANCE_NATIONAL,
          ].includes(watch('status'))}
        />
      </div>
      {(isSegreteriaNazionale(profile) || isDelegato(profile)) && (
        <div className='space-y-4'>
          <H4SpacingLetter>Spese della sede</H4SpacingLetter>
          <HeadquarterBalanceTable
            estimatedOutflow={estimatedHeadquarterOutflow}
            estimatedIncome={estimatedHeadquarterIncome}
            finalOutflow={finalHeadquarterOutflow}
            disabled={
              (disabled && disableEditConsuntivo) || isDelegato(profile)
            }
            safetyMargin={safetyMargin}
            estimatedBalanceOutlfow={estimatedOutflow}
            //per isCoursePublished si intende se il corso è stato pubblicato o consuntivato
            isCoursePublished={[
              CourseStatus.PUBLISHED,
              CourseStatus.FINAL_BALANCE_DELEGATION,
              CourseStatus.FINAL_BALANCE_NATIONAL,
            ].includes(watch('status'))}
          />
        </div>
      )}
      <div className='space-y-4'>
        <H4SpacingLetter>Prezzo vendita corso</H4SpacingLetter>
        <p className='mt-1 mb-0 italic text-orange-400 text-xs'>
          Il prezzo unitario comprende la spesa amministrativa se dovuta. Al
          prezzo unitario sarà aggiunto, se dovuto, il valore della tessera
          dell'anno in corso
        </p>

        <div className='grid md:grid-cols-3 items-start gap-4'>
          <PriceInputPresentational
            id=''
            label='Prezzo unitario di vendita'
            name=''
            type='number'
            value={bankTransferTotal}
            disabled
          />
          <div className='space-y-4'>
            <PriceInputPresentational
              id='subscriptionAmount'
              label='Prezzo unitario di vendita scontato per pagamento online'
              name='subscriptionAmount'
              type='number'
              value={watch('subscriptionAmount')}
              onChange={(e) =>
                setValue('subscriptionAmount', Number(e.target.value))
              }
              onBlur={() =>
                onChangeSubscriptionAmount(watch('subscriptionAmount'))
              }
              disabled={disabled || courseStatus === CourseStatus.PUBLISHED}
              error={errors.subscriptionAmount}
            />
            {showAlert && (
              <AlertMessage
                borderLeft
                noTitle
                text={`La quota corso non può essere minore di ${priceLimit}`}
              />
            )}
          </div>
          {/* <div className='md:col-span-2'>
            <p className='mb-0 text-gray-500 text-sm italic'>
              * Il prezzo unitario corrisponde al prezzo minimo pagato con
              carta.
              <br />
              {`Ogni pagamento diverso dal pagamento con carte subirà una
              maggiorazione del ${(settings?.bankTransferFee || 0) * 100}%.`}
            </p> */}
          {/* inserire la percentuale corretta della maggiorazione */}
          {/* </div> */}
        </div>
        <div className='mt-3 flex flex-wrap items-start gap-3'>
          <SimpleCheckboxPresentational
            id='freeCourse'
            name='freeCourse'
            label='Corso gratuito'
            checked={!!watch('freeCourse')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              setValue('freeCourse', checked, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (checked) {
                setValue('subscriptionAmount', 0, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                clearErrors('subscriptionAmount');
                setShowAlert(false);
              }
            }}
            disabled={disabled || !isDraftCourse}
          />
          <p className='mt-1 mb-0 italic text-orange-400 text-xs max-w-md'>
            Flaggando questa opzione potrai impostare il prezzo del corso a 0€
          </p>
        </div>
      </div>

      {/* <div className='space-y-4'>
        <H4SpacingLetter>Spesa amministrativa</H4SpacingLetter>

        <div className='grid md:grid-cols-3 items-end gap-4'>
          <PriceInputPresentational
            id='headquarterBalance.administrations.estimatedPriceUnit'
            label='Spesa amministrativa unitaria'
            name='headquarterBalance.administrations.estimatedPriceUnit'
            type='number'
            register={register}
            readOnly
            disabled
          />
        </div>
      </div> */}

      <div className='space-y-4'>
        <H4SpacingLetter>Modalità di pagamento</H4SpacingLetter>
        <p className='mt-1 mb-0 italic text-orange-400 text-xs'>
          Il prezzo unitario comprende la spesa amministrativa se dovuta. Al
          prezzo unitario sarà aggiunto, se dovuto, il valore della tessera
          dell'anno in corso
        </p>
        <RadioGroupPaymentCourse
          isCourseCanceled={disabled}
          sectionIban={sectionIban}
        />
      </div>

      <div className='space-y-4 bg-neutral-100 px-3 py-4'>
        <H4SpacingLetter>Andamento del corso</H4SpacingLetter>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
          <StaticInputPresentational
            big
            label='Margine preventivo'
            text={formatCurrency(utilePreventivato)}
          />
          <StaticInputPresentational
            big
            label='Margine consuntivo'
            text={formatCurrency(Number(utileConsuntivato))}
          />
          <StaticInputPresentational
            big
            label='Scostamento Margine'
            text={formatCurrency(utileConsuntivato - utilePreventivato)}
          />
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
          {
            <StaticInputPresentational
              big
              label='Tot spese di SEZIONE che evade la SEDE'
              text={formatCurrency(speseSezioneSede)}
            />
          }
          <StaticInputPresentational
            big
            label='Tot spese di SEZIONE che evade la SEZIONE'
            text={formatCurrency(speseSezioneSezione)}
          />
          {
            <StaticInputPresentational
              big
              label='Tot spese della SEDE che evade la SEDE'
              text={formatCurrency(speseSedeSede)}
            />
          }
          {
            <StaticInputPresentational
              big
              label='Totale spese'
              text={formatCurrency(totSpese)}
            />
          }
        </div>
      </div>
    </div>
  );
};

export default FormCostiCorso;
